import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAbortedRequest, useRpcCaller } from "../context/WidgetRequestContext";

/** A voucher that has been validated and applied to the booking. */
export interface AppliedVoucher {
  code: string;
  /** Amount the voucher redeems, in pence (minor currency units). */
  amountToRedeem: number;
}

interface UseVoucherRedemptionParams {
  /** Booking date (YYYY-MM-DD) forwarded to the redeemable check. */
  date: string;
  /** Venue id forwarded to the redeemable check. */
  venue: string;
  /** Base amount to pay (major currency units) before any voucher redemption. */
  baseAmount: number;
  /** Notified with the full applied-voucher list whenever it changes. */
  onVouchersChange?: (vouchers: AppliedVoucher[]) => void;
  /** Initial vouchers to restore (e.g., from URL parameters after redirect). */
  initialVouchers?: AppliedVoucher[];
}

/**
 * Encapsulates Soda voucher redemption: validating codes against Collins,
 * tracking applied vouchers, and computing the redeemed total / remaining
 * amount to pay on card. Mirrors DMN's checkVoucherValid /
 * updateAppliedVouchers logic from bookController.js.
 *
 * State lives here (used by the payment step) and is surfaced to the parent
 * booking form via `onVouchersChange` so the codes reach submission.
 */
export function useVoucherRedemption({
  date,
  venue,
  baseAmount,
  onVouchersChange,
  initialVouchers,
}: UseVoucherRedemptionParams) {
  const { t } = useTranslation();
  const call = useRpcCaller();
  const [appliedVouchers, setAppliedVouchers] = useState<AppliedVoucher[]>(initialVouchers ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror of `appliedVouchers` that is always current within the same tick.
  // `apply` awaits an async redeemable check; reading state from the callback
  // closure would be stale if `remove` ran while the request was in flight,
  // silently re-adding a removed voucher. The ref is the single source of
  // truth for building the next list, so concurrent updates compose correctly.
  const appliedVouchersRef = useRef<AppliedVoucher[]>(initialVouchers ?? []);

  // Commit a new voucher list, keeping the ref, state, and parent in sync.
  const commit = useCallback(
    (next: AppliedVoucher[]) => {
      appliedVouchersRef.current = next;
      setAppliedVouchers(next);
      onVouchersChange?.(next);
    },
    [onVouchersChange],
  );

  // Initialize with provided vouchers instead of always emptying.
  // Only sync to empty if no initial vouchers are provided.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only initialization
  useEffect(() => {
    const initial = initialVouchers ?? [];
    appliedVouchersRef.current = initial;
    setAppliedVouchers(initial);
    onVouchersChange?.(initial);

    // Re-validate restored vouchers to ensure they're still valid
    if (initial.length > 0) {
      validateRestoredVouchers(initial);
    }
  }, []);

  /** Re-checks one restored voucher, returning it with the latest amount, or null if no longer valid. */
  const revalidateVoucher = useCallback(
    async (voucher: AppliedVoucher): Promise<AppliedVoucher | null> => {
      try {
        const result = await call("/checkVoucherRedeemable", { date, venue, voucher: voucher.code });
        const stillValid =
          result.redeemable && result.amountToRedeem > 0 && result.code.toLowerCase() === voucher.code.toLowerCase();
        return stillValid ? { code: result.code, amountToRedeem: result.amountToRedeem } : null;
      } catch (error) {
        // Abandon the whole check so a partial list never replaces the guest's vouchers.
        if (isAbortedRequest(error)) throw error;
        console.warn(`Failed to validate restored voucher ${voucher.code}:`, error);
        return null;
      }
    },
    [call, date, venue],
  );

  // Validate restored vouchers by checking each one against the API
  const validateRestoredVouchers = useCallback(
    async (vouchers: AppliedVoucher[]) => {
      if (vouchers.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        const validVouchers: AppliedVoucher[] = [];

        for (const voucher of vouchers) {
          const revalidated = await revalidateVoucher(voucher);
          if (revalidated) validVouchers.push(revalidated);
        }

        // Check if any vouchers were removed or amounts changed
        const vouchersRemoved = validVouchers.length !== vouchers.length;
        const amountsChanged = vouchers.some((original, index) => {
          const updated = validVouchers[index];
          return updated && original.amountToRedeem !== updated.amountToRedeem;
        });

        // Update state if vouchers were removed or amounts changed
        if (vouchersRemoved || amountsChanged) {
          appliedVouchersRef.current = validVouchers;
          setAppliedVouchers(validVouchers);
          onVouchersChange?.(validVouchers);

          if (validVouchers.length === 0) {
            setError(
              t("booking_voucher_invalid_restored", "Some voucher codes are no longer valid and have been removed."),
            );
          } else if (vouchersRemoved) {
            setError(
              t("booking_voucher_some_invalid", "Some voucher codes are no longer valid and have been removed."),
            );
          }
        }
      } catch (error) {
        if (isAbortedRequest(error)) return;
        setError(t("booking_voucher_validation_error", "Couldn't validate voucher codes. Please try again."));
      } finally {
        setLoading(false);
      }
    },
    [revalidateVoucher, onVouchersChange, t],
  );

  /**
   * Total redeemed across all applied vouchers, in major currency units,
   * capped at the base amount. A voucher worth more than the bill only ever
   * offsets the bill, so the summary never shows more redeemed than is being
   * charged (e.g. an £80 voucher on a £50 payment displays as £50 redeemed).
   * Collins reconciles any surplus server-side from the submitted codes.
   */
  const redeemedTotal = useMemo(() => {
    const total = appliedVouchers.reduce((sum, voucher) => sum + voucher.amountToRedeem / 100, 0);
    return Math.min(total, baseAmount);
  }, [appliedVouchers, baseAmount]);

  /** Amount still payable on card after redemption (never negative). */
  const remainingAmount = Math.max(0, baseAmount - redeemedTotal);

  const apply = useCallback(
    async (rawCode: string) => {
      const code = rawCode.trim();
      if (!code) return;

      setError(null);
      if (appliedVouchersRef.current.some((voucher) => voucher.code.toLowerCase() === code.toLowerCase())) {
        setError(t("booking_voucher_duplicate", "This voucher has already been applied."));
        return;
      }

      setLoading(true);
      try {
        const result = await call("/checkVoucherRedeemable", { date, venue, voucher: code });
        if (!result.redeemable || result.amountToRedeem <= 0) {
          setError(t("booking_voucher_invalid", "This voucher code is not valid."));
          return;
        }
        // Re-read the latest list from the ref: a `remove` (or another `apply`)
        // may have run while the redeemable check was in flight. Skip if the
        // validated code is now already present to avoid duplicates from races.
        const current = appliedVouchersRef.current;
        if (current.some((voucher) => voucher.code.toLowerCase() === result.code.toLowerCase())) {
          return;
        }
        commit([...current, { code: result.code, amountToRedeem: result.amountToRedeem }]);
      } catch (error) {
        if (isAbortedRequest(error)) return;
        setError(t("booking_voucher_error", "We couldn't check that voucher. Please try again."));
      } finally {
        setLoading(false);
      }
    },
    [call, date, venue, commit, t],
  );

  const remove = useCallback(
    (code: string) => {
      setError(null);
      commit(appliedVouchersRef.current.filter((voucher) => voucher.code !== code));
    },
    [commit],
  );

  return { appliedVouchers, loading, error, apply, remove, redeemedTotal, remainingAmount };
}
